import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-4"
}
const _hoisted_2 = { class: "flex flex-col gap-1.5" }
const _hoisted_3 = { class: "text-[11px] font-normal uppercase tracking-[0.1em] text-foreground/65" }
const _hoisted_4 = {
  type: "button",
  class: "btn-bare flex h-10 w-full items-center justify-between rounded-md border border-border bg-background px-3 text-sm font-normal tracking-normal text-foreground transition-colors hover:bg-accent/50"
}
const _hoisted_5 = {
  key: 0,
  class: "flex items-center gap-2"
}
const _hoisted_6 = {
  key: 0,
  class: "inline-flex h-4 w-4 shrink-0 overflow-hidden rounded-full"
}
const _hoisted_7 = ["src", "alt"]
const _hoisted_8 = {
  key: 1,
  class: "text-muted-foreground"
}
const _hoisted_9 = { class: "border-b border-border px-3 py-2" }
const _hoisted_10 = ["placeholder"]
const _hoisted_11 = { class: "max-h-72 overflow-y-auto p-1" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = {
  key: 0,
  class: "inline-flex h-4 w-4 shrink-0 overflow-hidden rounded-full"
}
const _hoisted_14 = ["src", "alt"]
const _hoisted_15 = { class: "flex-1" }
const _hoisted_16 = {
  key: 0,
  class: "m-0 px-2 py-2 text-sm text-muted-foreground"
}
const _hoisted_17 = {
  key: 0,
  class: "flex flex-col gap-1.5"
}
const _hoisted_18 = { class: "text-[11px] font-normal uppercase tracking-[0.1em] text-foreground/65" }

import { ref, computed, watch } from 'vue';
import { Check, ChevronsUpDown } from 'lucide-vue-next';

import useCountrySwitcher from '@/composables/useCountrySwitcher';
import useWebsiteTexts from '@/composables/useWebsiteTexts';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Button from '@/components/Button.vue';

type Country = {
  id: string;
  current?: boolean;
  displayName?: string;
  channelName?: string;
  availableLanguages: Array<{ languageId: string; name?: string; current?: boolean }>;
};

const PENDING_KEY = 'localeSelectorPending';


export default /*@__PURE__*/_defineComponent({
  __name: 'LocaleSelector',
  emits: ["change"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const { availableCountries, changeCountry } = useCountrySwitcher();
const { websiteText } = useWebsiteTexts();

const selectedCountryId = ref('');
const selectedLanguageId = ref('');
const initialCountryId = ref('');
const initialLanguageId = ref('');
const submitting = ref(false);
const countryOpen = ref(false);
const countrySearch = ref('');

function readPendingSelection(): { countryId: string; languageId: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

watch(
  availableCountries,
  (countries) => {
    if (!countries?.length) return;

    // Prefer the selection the user just confirmed (persisted across the reload),
    // since the backend's `current` flag can be stale on the immediate next load.
    const pending = readPendingSelection();

    const current =
      (pending && (countries as Country[]).find((c) => c.id === pending.countryId)) ||
      (countries as Country[]).find((c) => c.current) ||
      (countries[0] as Country);
    selectedCountryId.value = current.id;
    initialCountryId.value = current.id;

    const langs = current.availableLanguages ?? [];
    const pendingLang =
      pending && langs.find((l: any) => l.languageId === pending.languageId);
    const currentLang = pendingLang || langs.find((l: any) => l.current) || langs[0];
    selectedLanguageId.value = currentLang?.languageId ?? '';
    initialLanguageId.value = currentLang?.languageId ?? '';
  },
  { immediate: true }
);

const selectedCountry = computed<Country | null>(() => {
  const list = availableCountries.value as Country[];
  return list.find((c) => c.id === selectedCountryId.value) || null;
});

const availableLanguages = computed(() => selectedCountry.value?.availableLanguages ?? []);

watch(selectedCountryId, () => {
  const langs = selectedCountry.value?.availableLanguages ?? [];
  const lang = langs.find((l) => l.current) || langs[0];
  selectedLanguageId.value = lang?.languageId ?? '';
});

const hasChanges = computed(() => selectedCountryId.value !== initialCountryId.value || selectedLanguageId.value !== initialLanguageId.value);

function countryName(c: Country) {
  return c.displayName || c.channelName || c.id;
}

function flagSrc(id: string) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    return require(`@/static/flags/${id.toLowerCase()}.svg`);
  } catch {
    return '';
  }
}

const filteredCountries = computed(() => {
  const list = availableCountries.value as Country[];
  const q = countrySearch.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter((c) => countryName(c).toLowerCase().includes(q) || c.id.toLowerCase().includes(q));
});

function selectCountry(id: string) {
  selectedCountryId.value = id;
  countryOpen.value = false;
  countrySearch.value = '';
}

async function changeCountryWithRetry(countryId: string, languageId: string) {
  try {
    return await changeCountry(countryId, languageId);
  } catch {
    // The backend setCountry mutation rejects the first attempt when the
    // session/cart state isn't seeded yet; a single retry reliably succeeds.
    return await changeCountry(countryId, languageId);
  }
}

async function onConfirm() {
  if (!hasChanges.value || submitting.value) return;
  submitting.value = true;
  try {
    const redirectUrl = await changeCountryWithRetry(selectedCountryId.value, selectedLanguageId.value);
    emit('change');

    // Persist the chosen locale so the picker reflects it after the reload,
    // even if the freshly-fetched country-switcher data has a stale `current` flag.
    try {
      window.sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          countryId: selectedCountryId.value,
          languageId: selectedLanguageId.value,
        }),
      );
    } catch {
      /* sessionStorage unavailable — non-fatal */
    }

    if (redirectUrl) {
      window.location.assign(redirectUrl);
    } else {
      window.location.reload();
    }
  } catch (err) {
    console.error('Failed to change country/language', err);
  } finally {
    submitting.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_unref(availableCountries).length >= 1)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("label", _hoisted_3, _toDisplayString(_unref(websiteText)('countryswitcher__country').value), 1),
          _createVNode(_unref(Popover), {
            open: countryOpen.value,
            "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((countryOpen).value = $event))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(PopoverTrigger), { "as-child": "" }, {
                default: _withCtx(() => [
                  _createElementVNode("button", _hoisted_4, [
                    (selectedCountry.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
                          (flagSrc(selectedCountry.value.id))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_6, [
                                _createElementVNode("img", {
                                  src: flagSrc(selectedCountry.value.id),
                                  alt: countryName(selectedCountry.value),
                                  class: "h-full w-full object-cover"
                                }, null, 8, _hoisted_7)
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("span", null, _toDisplayString(countryName(selectedCountry.value)), 1)
                        ]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_unref(websiteText)('countryswitcher__country').value || 'Select country…'), 1)),
                    _createVNode(_unref(ChevronsUpDown), { class: "ml-2 h-4 w-4 shrink-0 opacity-50" })
                  ])
                ]),
                _: 1
              }),
              _createVNode(_unref(PopoverContent), {
                align: "start",
                "side-offset": 4,
                class: "z-[10001] w-[var(--reka-popover-trigger-width)] border-border bg-background p-0 text-foreground"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_9, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((countrySearch).value = $event)),
                      type: "text",
                      placeholder: _unref(websiteText)('countryswitcher__country').value || 'Search country…',
                      class: "w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    }, null, 8, _hoisted_10), [
                      [_vModelText, countrySearch.value]
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_11, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((filteredCountries.value as Country[]), (c) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: c.id,
                        type: "button",
                        class: "btn-bare flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm font-normal tracking-normal text-foreground hover:bg-accent",
                        onClick: ($event: any) => (selectCountry(c.id))
                      }, [
                        (flagSrc(c.id))
                          ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                              _createElementVNode("img", {
                                src: flagSrc(c.id),
                                alt: countryName(c),
                                class: "h-full w-full object-cover"
                              }, null, 8, _hoisted_14)
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("span", _hoisted_15, _toDisplayString(countryName(c)), 1),
                        (c.id === selectedCountryId.value)
                          ? (_openBlock(), _createBlock(_unref(Check), {
                              key: 1,
                              class: "h-4 w-4"
                            }))
                          : _createCommentVNode("", true)
                      ], 8, _hoisted_12))
                    }), 128)),
                    (filteredCountries.value.length === 0)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(_unref(websiteText)('countryswitcher__nocountry').value || 'No results'), 1))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["open"])
        ]),
        (availableLanguages.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
              _createElementVNode("label", _hoisted_18, _toDisplayString(_unref(websiteText)('countryswitcher__language').value), 1),
              _createVNode(_unref(Select), {
                modelValue: selectedLanguageId.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedLanguageId).value = $event))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SelectTrigger), { class: "h-10 rounded-sm border-border bg-background font-normal tracking-normal text-foreground transition-colors hover:bg-accent/50" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SelectValue), {
                        placeholder: _unref(websiteText)('countryswitcher__language').value
                      }, null, 8, ["placeholder"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(SelectContent), { class: "z-[10001] border-border bg-background text-foreground" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableLanguages.value, (lang) => {
                        return (_openBlock(), _createBlock(_unref(SelectItem), {
                          key: lang.languageId,
                          value: lang.languageId,
                          class: "font-normal tracking-normal"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(lang.name || lang.languageId), 1)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]))
          : _createCommentVNode("", true),
        _createVNode(Button, {
          variant: "primary",
          size: "md",
          class: "w-full rounded-sm disabled:bg-foreground disabled:text-background disabled:cursor-not-allowed",
          state: !hasChanges.value ? 'disabled' : submitting.value ? 'pending' : 'idle',
          onClick: onConfirm
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(websiteText)('countryswitcher__location').value || 'Change location'), 1)
          ]),
          _: 1
        }, 8, ["state"])
      ]))
    : _createCommentVNode("", true)
}
}

})